import React from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Clock, Users, DollarSign, ShieldCheck, Smile, Cloud, ShoppingBag, Gavel, CheckCircle2 } from 'lucide-react';
import { BrandIcon } from '../components/BrandIcon';
import { AiSupport } from '../components/AiSupportIcon';
import { Testimonials } from '../components/Testimonials';

const Impact = () => {
  // Illustrative revenue trajectory (past in-house role, not a specific client)
  const revenueGrowthData = [
    { month: 'Pre-CX', revenue: 120 },
    { month: '3 Months', revenue: 130 },
    { month: '6 Months', revenue: 150 },
    { month: '9 Months', revenue: 170 },
    { month: '12 Months', revenue: 182 },
  ];

  // Illustrative satisfaction improvement (past in-house role, not a specific client)
  const satisfactionData = [
    { category: 'Response Time', Before: 65, After: 88 },
    { category: 'Resolution', Before: 58, After: 92 },
    { category: 'Knowledge', Before: 70, After: 92 },
    { category: 'Courtesy', Before: 62, After: 94 },
    { category: 'Overall', Before: 66, After: 91 },
  ];

  const keyMetrics = [
    {
      icon: Smile,
      value: '94%',
      label: 'Average CSAT',
      description: 'Across support operations I led'
    },
    {
      icon: DollarSign,
      value: '$2.87M',
      label: 'Revenue Impact',
      description: 'Delivered through CX initiatives'
    },
    {
      icon: Clock,
      value: '36%',
      label: 'Faster Resolution',
      description: 'Reduction in resolution times'
    },
    {
      icon: AiSupport,
      value: '25%',
      label: 'Fewer Tickets',
      description: 'Ticket volume reduced through AI'
    },
    {
      icon: Users,
      value: '4 → 55+',
      label: 'Support Team Scaled',
      description: 'Across 6 time zones'
    },
    {
      icon: ShieldCheck,
      value: '91%',
      label: 'SLA Compliance',
      description: 'Sustained across teams'
    }
  ];

  const caseStudyStats = [
    {
      value: '10 → 1',
      label: 'Scheduling Touchpoints',
      description: 'Cut from 7 emails, a call, and 2 texts down to a single payment confirmation'
    },
    {
      value: '6 days → 24-48 hrs',
      label: 'Full Intake Cycle',
      description: 'From first contact to confirmed appointment, schedule through documentation'
    },
    {
      value: '0 → Full Visibility',
      label: 'Site Analytics',
      description: 'Geolocation-based traffic and click tracking, replacing zero prior insight'
    },
    {
      value: '+25%',
      label: 'Revenue & Consultation Volume',
      description: 'Growth in the first week after launch'
    }
  ];

  const industries = [
    {
      icon: Cloud,
      title: 'SaaS',
      description: 'Built CX departments from the ground up for subscription-based companies, with a focus on scalable support systems that reduce churn as the customer base grows.'
    },
    {
      icon: ShoppingBag,
      title: 'eCommerce',
      description: 'Designed customer journeys covering the full path from browsing to post-purchase support, built to drive repeat business and reduce cart-to-complaint friction.'
    },
    {
      icon: Gavel,
      title: 'Auction & Marketplace',
      description: 'Delivered real-time support for high-stakes, time-sensitive transactions, with escalation protocols built for the pressure of live auction environments.'
    }
  ];

  return (
    <div className="min-h-screen py-20 md:py-32 bg-white" data-testid="impact-page">
      <div className="container mx-auto px-[7.6923%]">
        {/* Header — Track Record */}
        <div className="text-center mb-20">
          <h1 className="text-[36px] sm:text-[32px] md:text-[48px] lg:text-[66px] font-semibold leading-[1.1] tracking-[-0.62px] text-gray-900 mb-6">
            The Track Record Behind CX Sage
          </h1>
          <p className="text-[20px] text-gray-600 max-w-3xl mx-auto">
            Before founding CX Sage, I spent 15+ years building and running CX departments in-house. Here's what that work produced:
          </p>
        </div>

        {/* Track Record Metrics Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-32">
          {keyMetrics.map((metric, index) => (
            <div
              key={index}
              data-testid={`track-record-metric-${index}`}
              className="bg-gray-50 p-8 border border-gray-200 hover:border-[#00D4B4] hover:shadow-lg transition-all duration-400"
            >
              <BrandIcon icon={metric.icon} className="mb-4" />
              <div className="text-[32px] md:text-[48px] font-semibold text-gray-900 leading-none mb-2">
                {metric.value}
              </div>
              <div className="text-[20px] font-medium text-gray-900 mb-2">
                {metric.label}
              </div>
              <div className="text-[16px] text-gray-600">
                {metric.description}
              </div>
            </div>
          ))}
        </div>

        {/* Experience by Industry */}
        <div className="mb-32">
          <h2 className="text-[32px] md:text-[48px] font-semibold leading-[1.1] text-gray-900 mb-4">
            Experience by Industry
          </h2>
          <p className="text-[18px] text-gray-600 mb-12">
            How I've applied CX across different business models
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <div
                key={index}
                data-testid={`industry-card-${index}`}
                className="bg-gray-50 p-10 border border-gray-200 hover:border-[#00D4B4] hover:shadow-lg transition-all duration-400 group"
              >
                <BrandIcon icon={industry.icon} className="mb-6 group-hover:scale-110 transition-transform duration-400" />
                <h3 className="text-[24px] font-semibold text-gray-900 mb-4">{industry.title}</h3>
                <p className="text-[16px] text-gray-600 leading-[1.6]">
                  {industry.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Featured Case Study (real, verified) */}
        <div className="mb-32" data-testid="case-study-section">
          <div className="bg-[#085041] p-8 sm:p-12 lg:p-16 shadow-xl">
            <div className="inline-flex items-center gap-2 bg-[#00D4B4] text-[#043029] text-[13px] font-semibold uppercase tracking-wider px-4 py-2 mb-8">
              <CheckCircle2 className="w-4 h-4" /> A real, verified project — not an illustration
            </div>
            <h2 className="text-[32px] md:text-[48px] font-semibold leading-[1.1] text-white mb-6">
              Featured Case Study: Legal Services Digital Transformation
            </h2>
            <p className="text-[18px] text-[#E1F5EE]/90 leading-[1.7] max-w-4xl mb-12">
              Before this project, the firm ran entirely on manual, analog processes: scheduling by phone and email, paper intake, and no visibility into how clients found the practice. I independently designed and built the full digital transformation using Claude, Emergent, and Calendly.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {caseStudyStats.map((stat, index) => (
                <div
                  key={index}
                  data-testid={`case-study-stat-${index}`}
                  className="bg-white/5 border border-white/10 p-6 backdrop-blur-sm hover:bg-white/10 transition-colors duration-400"
                >
                  <div className="text-[24px] md:text-[28px] font-semibold text-[#00D4B4] leading-tight mb-3">
                    {stat.value}
                  </div>
                  <div className="text-[17px] font-medium text-white mb-2">
                    {stat.label}
                  </div>
                  <div className="text-[14px] text-[#E1F5EE]/70 leading-[1.5]">
                    {stat.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Revenue Growth Timeline (illustrative) */}
        <div className="mb-32">
          <h2 className="text-[32px] md:text-[48px] font-semibold leading-[1.1] text-gray-900 mb-4">
            Revenue Growth Timeline
          </h2>
          <p className="text-[18px] text-gray-600 mb-12">
            Illustrative — a representative revenue trajectory over 12 months of CX department implementation from a past in-house role.
          </p>
          <div className="bg-gray-50 p-6 sm:p-12 border border-gray-200 shadow-md">
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={revenueGrowthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis dataKey="month" stroke="#4A4A4A" />
                <YAxis stroke="#4A4A4A" label={{ value: 'Monthly Revenue ($K)', angle: -90, position: 'insideLeft', fill: '#4A4A4A' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  labelStyle={{ color: '#1A1A1A' }}
                />
                <Legend />
                <Line type="monotone" dataKey="revenue" name="Monthly Revenue ($K)" stroke="#00D4B4" strokeWidth={3} dot={{ fill: '#00D4B4', r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Customer Satisfaction Improvement (illustrative) */}
        <div className="mb-32">
          <h2 className="text-[32px] md:text-[48px] font-semibold leading-[1.1] text-gray-900 mb-4">
            Customer Satisfaction Improvement
          </h2>
          <p className="text-[18px] text-gray-600 mb-12">
            Illustrative — representative improvements across satisfaction metrics after implementation in past in-house roles.
          </p>
          <div className="bg-gray-50 p-6 sm:p-12 border border-gray-200 shadow-md">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={satisfactionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis dataKey="category" stroke="#4A4A4A" />
                <YAxis stroke="#4A4A4A" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                  labelStyle={{ color: '#1A1A1A' }}
                />
                <Legend />
                <Bar dataKey="Before" name="Before Implementation" fill="rgba(0,0,0,0.1)" />
                <Bar dataKey="After" name="After Implementation" fill="#00D4B4" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Approved client testimonials */}
        <Testimonials />

        {/* Bottom CTA */}
        <div className="text-center mt-32">
          <div className="inline-block bg-gradient-to-br from-[#00D4B4] to-[#00BFA3] p-12 max-w-4xl shadow-xl">
            <h3 className="text-[24px] md:text-[32px] font-semibold text-white mb-4">
              Want This Kind of Impact?
            </h3>
            <p className="text-[18px] text-white/95 mb-8">
              Let's discuss how I can build a world-class CX department tailored to your organization.
            </p>
            <a
              href="/contact"
              className="inline-flex bg-white text-[#00D4B4] px-8 py-5 text-lg font-medium hover:bg-gray-100 transition-all duration-400 items-center gap-3"
              style={{ borderRadius: '0px' }}
            >
              Get Started Today
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Impact;
